<template>
  <v-navigation-drawer
    v-model="isOpen"
    right
    fixed
    temporary
    width="390"
    class="cart-drawer"
  >
    <!-- Header -->
    <div class="drawer-header d-flex align-center justify-space-between px-5 py-4">
      <div class="d-flex align-center">
        <v-icon color="#2E7D32" class="mr-2">mdi-cart</v-icon>
        <span class="drawer-title">Your Cart</span>
        <v-chip v-if="cart.count > 0" x-small color="#2E7D32" dark class="ml-2">
          {{ cart.count }}
        </v-chip>
      </div>
      <v-btn icon @click="cart.closeDrawer()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-divider></v-divider>

    <!-- Empty state -->
    <div v-if="cart.items.length === 0" class="empty-state text-center pa-10">
      <v-icon size="72" color="grey lighten-2">mdi-cart-outline</v-icon>
      <p class="empty-text mt-4 mb-1">Your cart is empty</p>
      <p class="empty-sub">Add a service to get started</p>
      <v-btn depressed color="#2E7D32" dark class="mt-4" small @click="cart.closeDrawer(); $router.push('/store')">
        Browse Services
      </v-btn>
    </div>

    <!-- Items list -->
    <div v-else class="d-flex flex-column" style="height: calc(100% - 65px);">
      <div class="cart-items flex-grow-1 overflow-y-auto">
        <div
          v-for="item in cart.items"
          :key="item.id"
          class="cart-item px-5 py-4"
        >
          <div class="d-flex justify-space-between align-start">
            <div>
              <p class="item-title mb-1">{{ item.title }}</p>
              <v-chip x-small class="duration-chip">
                <v-icon left x-small>mdi-clock-outline</v-icon>
                {{ item.duration }}
              </v-chip>
            </div>
            <v-btn icon x-small @click="cart.remove(item.id)" class="ml-2 mt-n1">
              <v-icon size="18" color="grey darken-1">mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="d-flex justify-space-between align-center mt-3">
            <div class="qty-controls d-flex align-center">
              <v-btn
                icon
                x-small
                outlined
                @click="cart.setQty(item.id, item.qty - 1)"
                class="qty-btn"
              >
                <v-icon size="16">mdi-minus</v-icon>
              </v-btn>
              <span class="qty-value mx-3">{{ item.qty }}</span>
              <v-btn
                icon
                x-small
                outlined
                @click="cart.setQty(item.id, item.qty + 1)"
                class="qty-btn"
              >
                <v-icon size="16">mdi-plus</v-icon>
              </v-btn>
            </div>
            <span class="item-price">${{ (item.price * item.qty).toFixed(2) }}</span>
          </div>
          <v-divider class="mt-4"></v-divider>
        </div>
      </div>

      <!-- Footer: total + checkout -->
      <div class="cart-footer px-5 py-5">
        <div class="d-flex justify-space-between align-center mb-1">
          <span class="subtotal-label">Subtotal</span>
          <span class="subtotal-amount">${{ cart.total.toFixed(2) }} USD</span>
        </div>
        <p class="tax-note mb-4">Taxes and fees calculated at checkout</p>

        <v-btn
          class="checkout-btn mb-2"
          block
          depressed
          large
          :loading="loading"
          :disabled="loading"
          @click="goCheckout"
        >
          <v-icon left>mdi-lock-outline</v-icon>
          Secure Checkout
        </v-btn>
        <v-btn text block small color="grey darken-1" @click="cart.closeDrawer()">
          Continue Shopping
        </v-btn>
      </div>
    </div>

    <!-- Error snackbar -->
    <v-snackbar v-model="showError" color="error" top timeout="5000">
      {{ errorMsg }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showError = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-navigation-drawer>
</template>

<script>
import { cart } from '@/cart';
import { waitForAuth } from '@/firebase';
import axios from 'axios';

export default {
  name: 'CartDrawer',

  data() {
    return {
      cart,
      loading: false,
      showError: false,
      errorMsg: '',
      resumeCheckoutPending: false,
    };
  },

  watch: {
    '$route.query.checkout'(value) {
      if (value !== '1' || this.resumeCheckoutPending) return;

      this.resumeCheckoutPending = true;
      this.$router.replace({ path: this.$route.path, query: {
        ...this.$route.query,
        checkout: undefined,
      } }).then(() => this.goCheckout()).catch(() => {
        this.resumeCheckoutPending = false;
      });
    },
  },

  computed: {
    isOpen: {
      get() { return cart.drawerOpen; },
      set(v) { v ? cart.openDrawer() : cart.closeDrawer(); },
    },
  },

  methods: {
    async goCheckout() {
      this.loading = true;
      try {
        const user = await waitForAuth();
        if (!user) {
          cart.closeDrawer();
          await this.$router.push({
            name: 'login',
            query: { redirect: '/store?checkout=1' },
          });
          return;
        }

        const payload = {
          items: cart.items.map(i => ({ product_id: i.id, qty: i.qty })),
        };
        const token = await user.getIdToken();

        const { data } = await axios.post(
          process.env.VUE_APP_API_URL + '/checkout.php',
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.href = data.url;
      } catch (err) {
        this.errorMsg =
          err.response?.data?.error || 'Could not start checkout. Please try again.';
        this.showError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cart-drawer {
  border-left: 1px solid #e0e0e0;
}

.drawer-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1a1a;
}

/* Empty */
.empty-state { color: #999; }
.empty-text  { font-weight: 600; font-size: 1rem; color: #555; }
.empty-sub   { font-size: 0.85rem; color: #999; margin: 0; }

/* Item */
.cart-item { background: #fff; }
.cart-item:hover { background: #fafafa; }

.item-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1a1a1a;
  line-height: 1.3;
}

.duration-chip {
  background-color: #e8f5e9 !important;
  color: #2E7D32 !important;
  font-weight: 600;
  font-size: 0.7rem;
}

.qty-controls { gap: 0; }

.qty-btn {
  border-color: #ccc !important;
  color: #555 !important;
}

.qty-value {
  font-weight: 700;
  font-size: 1rem;
  color: #1a1a1a;
  min-width: 24px;
  text-align: center;
}

.item-price {
  font-weight: 700;
  font-size: 1rem;
  color: #1B5E20;
}

/* Footer */
.cart-footer {
  border-top: 1px solid #e0e0e0;
  background: #fff;
}

.subtotal-label  { font-weight: 600; font-size: 1rem; color: #1a1a1a; }
.subtotal-amount { font-weight: 800; font-size: 1.2rem; color: #1B5E20; }

.tax-note {
  font-size: 0.78rem;
  color: #999;
  margin: 0;
}

.checkout-btn {
  background-color: #2E7D32 !important;
  color: white !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  border-radius: 10px !important;
  height: 52px !important;
}

.checkout-btn:hover:not(:disabled) {
  background-color: #1B5E20 !important;
  box-shadow: 0 6px 16px rgba(46, 125, 50, 0.3) !important;
}
</style>
